import React, {
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import AirtelPaymentForm, {
  AirtelInfo,
  AirtelPaymentFormRef,
} from './AirtelPaymentForm';
import CardPaymentForm, {CardInfo, CardPaymentFormRef} from './CardPaymentForm';
import CashiaWalletForm, {
  CashiaInfo,
  CashiaWalletFormRef,
} from './CashiaWalletForm';
import DigitalWalletForm, {DigitalWalletFormRef} from './DigitalWalletForm';
import MPesaPaymentForm, {
  MPesaInfo,
  MPesaPaymentFormRef,
} from './MpesaPaymentForm';
import Airtel from '../../../../assets/Airtel.svg';
import Mpesa from '../../../../assets/Mpesa.svg';
import MasterCard from '../../../../assets/icons/MasterCard.svg';
import Visa from '../../../../assets/icons/VisaCard.svg';
import Cards from '../../../../assets/icons/card.svg';
import Wallet from '../../../../assets/icons/wallet.svg';
import Cashia from '../../../../assets/logos/cashia_logo_pink.svg';
import Mobile from '../../../../assets/mobile.svg';
import CustomRadioInput from '../../../../components/common/CustomRadioInput';
import {CheckoutResponse, CheckoutTxStatusResponse} from '../api';

export type PaymentMethod =
  | 'card'
  | 'mpesa'
  | 'cashia'
  | 'airtel'
  | 'apple-pay'
  | 'google-pay';

// Export ref interface for parent to access validation
export interface PaymentMethodSelectorRef {
  validateCurrentMethod: () => boolean;
  hasErrors: () => boolean;
  processDigitalWalletPayment: () => Promise<void>;
}

const PaymentMethodSelector = forwardRef<
  PaymentMethodSelectorRef,
  {
    checkoutData: CheckoutResponse;
    paymentMethod: PaymentMethod;
    setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
    cardInfo: CardInfo;
    setCardInfo: React.Dispatch<React.SetStateAction<CardInfo>>;
    mpesaInfo: MPesaInfo;
    setMPesaInfo: React.Dispatch<React.SetStateAction<MPesaInfo>>;
    cashiaInfo: CashiaInfo;
    setCashiaInfo: React.Dispatch<React.SetStateAction<CashiaInfo>>;
    airtelInfo: AirtelInfo;
    setAirtelInfo: React.Dispatch<React.SetStateAction<AirtelInfo>>;
    reference: string;
    onBinCheck?: (requiresAddress: boolean) => void;
    onDigitalWalletSuccess?: (result: CheckoutTxStatusResponse) => void;
    onDigitalWalletError?: (error: unknown) => void;
    onStatusChange?: (status: string) => void;
  }
>((props, ref) => {
  const {
    checkoutData,
    paymentMethod,
    setPaymentMethod,
    cardInfo,
    setCardInfo,
    mpesaInfo,
    setMPesaInfo,
    airtelInfo,
    setAirtelInfo,
    cashiaInfo,
    setCashiaInfo,
    reference,
    onBinCheck,
    onDigitalWalletSuccess,
    onDigitalWalletError,
    onStatusChange,
  } = props;

  // Refs to access child component validation
  const cardFormRef = useRef<CardPaymentFormRef>(null);
  const mpesaFormRef = useRef<MPesaPaymentFormRef>(null);
  const airtelFormRef = useRef<AirtelPaymentFormRef>(null);
  const cashiaFormRef = useRef<CashiaWalletFormRef>(null);
  const digitalWalletFormRef = useRef<DigitalWalletFormRef>(null);

  // Expose validation methods to parent
  useImperativeHandle(ref, () => ({
    validateCurrentMethod: () => {
      if (paymentMethod === 'card') {
        return cardFormRef.current ? cardFormRef.current.validateAll() : false;
      } else if (paymentMethod === 'mpesa') {
        return mpesaFormRef.current
          ? mpesaFormRef.current.validateAll()
          : false;
      } else if (paymentMethod === 'cashia') {
        return cashiaFormRef.current
          ? cashiaFormRef.current.validateAll()
          : false;
      } else if (paymentMethod === 'airtel') {
        return airtelFormRef.current
          ? airtelFormRef.current.validateAll()
          : false;
      } else if (
        paymentMethod === 'apple-pay' ||
        paymentMethod === 'google-pay'
      ) {
        return digitalWalletFormRef.current
          ? digitalWalletFormRef.current.validateAll()
          : true;
      }
      return false;
    },

    hasErrors: () => {
      if (paymentMethod === 'card') {
        return cardFormRef.current ? cardFormRef.current.hasErrors() : false;
      } else if (paymentMethod === 'mpesa') {
        return mpesaFormRef.current ? mpesaFormRef.current.hasErrors() : false;
      } else if (paymentMethod === 'cashia') {
        return cashiaFormRef.current
          ? cashiaFormRef.current.hasErrors()
          : false;
      } else if (paymentMethod === 'airtel') {
        return airtelFormRef.current
          ? airtelFormRef.current.hasErrors()
          : false;
      } else if (
        paymentMethod === 'apple-pay' ||
        paymentMethod === 'google-pay'
      ) {
        return digitalWalletFormRef.current
          ? digitalWalletFormRef.current.hasErrors()
          : false;
      }
      return false;
    },

    processDigitalWalletPayment: async () => {
      if (
        digitalWalletFormRef.current &&
        (paymentMethod === 'apple-pay' || paymentMethod === 'google-pay')
      ) {
        await digitalWalletFormRef.current.processPayment();
      }
    },
  }));
  // const isKES = checkoutData.currency === 'KES';
  const isKES = true;
  const amount = checkoutData.amount;
  const enableDigitalWallets =
    import.meta.env.VITE_ENABLE_DIGITAL_WALLETS === 'true';

  const ENABLED_PAYMENT_METHODS = useMemo(() => {
    const methods: PaymentMethod[] = [];

    methods.push('cashia');

    if (!isKES || amount > 250000) {
      methods.push('card');
    } else if (isKES && amount < 100) {
      methods.push('mpesa');
      methods.push('airtel');
    } else {
      methods.push('card', 'mpesa', 'airtel');
    }

    if (enableDigitalWallets) {
      // Check if Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const hasApplePaySession =
        typeof window !== 'undefined' && !!window.ApplePaySession;
      // canMakePayments() can throw in certain contexts (non-HTTPS, misconfigured, etc.)
      let canMakePayments = false;
      if (hasApplePaySession) {
        try {
          canMakePayments = window.ApplePaySession?.canMakePayments() ?? false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Apple Pay canMakePayments() check failed:', error);
        }
      }
      canMakePayments = true;

      // eslint-disable-next-line no-console
      console.log('V2 Apple Pay Check:', {
        isSafari,
        hasApplePaySession,
        canMakePayments,
        userAgent: navigator.userAgent,
      });

      // Only show Apple Pay on Safari
      if (isSafari && canMakePayments) {
        methods.push('apple-pay');
      }
      methods.push('google-pay');
    }
    return methods;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKES, amount, enableDigitalWallets]);
  const PAYMENT_METHOD_DETAILS: Record<
    string,
    {label: string; icon?: JSX.Element; logo?: JSX.Element}
  > = {
    card: {
      label: 'Card',
      icon: <img src={Cards} alt="Cards" className="h-4 mr-2" />,
      logo: (
        <>
          <img src={MasterCard} alt="Mastercard" className="h-4" />
          <img src={Visa} alt="Visa" className="h-4 ml-1" />
        </>
      ),
    },
    mpesa: {
      label: 'M-Pesa',
      icon: <img src={Mobile} alt="mobile" className="h-4 mr-2" />,
      logo: <img src={Mpesa} alt="M-Pesa" className="ml-auto h-4" />,
    },
    airtel: {
      label: 'Airtel Money',
      icon: <img src={Mobile} alt="mobile" className="h-4 mr-2" />,
      logo: <img src={Airtel} alt="Airtel Money" className="ml-auto h-4" />,
    },
    cashia: {
      label: 'Cashia',
      icon: <img src={Wallet} alt="wallet" className="h-4 mr-2" />,
      logo: <img src={Cashia} alt="Cashia" className="ml-auto h-4" />,
    },
    'apple-pay': {
      label: 'Apple Pay',
      icon: <span className="h-4 mr-2 text-lg">🍎</span>,
      logo: <span className="ml-auto text-sm font-semibold">Pay</span>,
    },
    'google-pay': {
      label: 'Google Pay',
      icon: <span className="h-4 mr-2 text-lg">G</span>,
      logo: <span className="ml-auto text-sm font-semibold">Pay</span>,
    },
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('V2 PaymentSelector mounted/updated', {
      enableDigitalWallets,
      ENABLED_PAYMENT_METHODS,
    });
  }, [enableDigitalWallets, ENABLED_PAYMENT_METHODS]);

  useEffect(() => {
    const firstMethod = ENABLED_PAYMENT_METHODS[0];
    if (firstMethod !== undefined) {
      setPaymentMethod(firstMethod);
    }
  }, [ENABLED_PAYMENT_METHODS, setPaymentMethod]);

  return (
    <div className="bg-transparent w-full mb-6">
      <h3 className="text-[16px] font-semibold mb-4 text-headlineGrayscale">
        Payment Method
      </h3>

      <div className="rounded-lg outline outline-[1px] outline-offset-[-0.80px] outline-subtleGrey inline-flex flex-col justify-start items-start overflow-hidden w-full">
        {ENABLED_PAYMENT_METHODS.map((item, idx) => (
          <div
            key={idx}
            className={`p-3 lg:p-4 w-full ${
              idx !== ENABLED_PAYMENT_METHODS.length - 1
                ? 'border-b-[1px] border-subtleGrey'
                : ''
            }`}>
            <CustomRadioInput
              checked={paymentMethod === item}
              handleChange={() => setPaymentMethod(item)}
              item={item}
              methodDetails={PAYMENT_METHOD_DETAILS}
            />

            {item === 'card' && paymentMethod === 'card' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <CardPaymentForm
                  ref={cardFormRef}
                  cardInfo={cardInfo}
                  setCardInfo={setCardInfo}
                  checkoutData={checkoutData}
                  onBinCheck={onBinCheck}
                />
              </div>
            )}
            {item === 'mpesa' && paymentMethod === 'mpesa' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <MPesaPaymentForm
                  ref={mpesaFormRef}
                  mpesaInfo={mpesaInfo}
                  setMPesaInfo={setMPesaInfo}
                />
              </div>
            )}

            {item === 'airtel' && paymentMethod === 'airtel' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <AirtelPaymentForm
                  ref={airtelFormRef}
                  airtelInfo={airtelInfo}
                  setAirtelInfo={setAirtelInfo}
                />
              </div>
            )}

            {item === 'cashia' && paymentMethod === 'cashia' && (
              <div className="mt-3 mb-0 lg:mb-1">
                <CashiaWalletForm
                  ref={cashiaFormRef}
                  cashiaInfo={cashiaInfo}
                  setCashiaInfo={setCashiaInfo}
                  checkoutData={checkoutData}
                  reference={reference}
                  onStatusChange={onStatusChange}
                />
              </div>
            )}

            {(item === 'apple-pay' || item === 'google-pay') &&
              (paymentMethod === 'apple-pay' ||
                paymentMethod === 'google-pay') && (
                <div className="mt-3 mb-0 lg:mb-1">
                  <DigitalWalletForm
                    ref={digitalWalletFormRef}
                    sessionData={{
                      sessionId: checkoutData.sessionId,
                      amount: checkoutData.amount,
                      currency: checkoutData.currency,
                    }}
                    walletType={paymentMethod}
                    onSuccess={onDigitalWalletSuccess}
                    onError={onDigitalWalletError}
                  />
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
});

PaymentMethodSelector.displayName = 'PaymentMethodSelector';

export default PaymentMethodSelector;
